import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { FinalGrade } from 'app/library/final-grade/models/final-grade.model';
import { ModuleManagersService } from 'app/library/module-managers/services/module-managers.service';
import { ModuleUserProgressStatus } from 'app/library/module-user/enums/module-user-progress-status.enum';
import { ModuleCompletitionStatuses } from 'app/library/module-user/models/module-completition-status';
import { ModuleUserCategoryStatus } from 'app/library/module-user/models/module-user-category-status';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { TeamCourseMemberDetailsDialogComponent } from 'app/main/pages/modules/module-form/module-form-participants-tab/team-course-member-details-dialog/team-course-member-details-dialog.component';
import { TeamProgramMemberDetailsDialogComponent } from 'app/main/pages/modules/module-form/module-form-participants-tab/team-program-member-details-dialog/team-program-member-details-dialog.component';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { IMaxBrainEnvironment } from 'app/projects/shared/src/lib/interfaces/environment.interface';
import { ILanguage, ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { CalculateDurationPipe } from 'app/projects/shared/src/lib/pipes/calculate-duration.pipe';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import { User } from 'app/projects/user/src/lib/models/user';
import { BehaviorSubject, timer } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { ModuleKind } from '../../enums/module-kind.enum';
import { ModuleType } from '../../enums/moduleType.enum';
import { Module } from '../../models/module.model';
import { ModuleGlobalService } from '../../services/module-global.service';

@Component({
    selector: 'app-modules-card',
    templateUrl: './cards.component.html',
    styleUrls: ['./cards.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleCardsListComponent extends UnsubscribeOnDestroy implements AfterContentInit {
    @Input()
    module: Module;
    duration: string;
    responsibleManagersString: string;

    @Input()
    finalGrade: FinalGrade;

    @Input()
    myModuleUser: ModuleUser;

    @Input()
    isPinned: boolean;

    @Input()
    showPinModule: boolean;

    @Input()
    isCertificateOfAttendanceFeatureEnabled: boolean;

    @Input()
    isTeamMembersPage: boolean;

    @Input()
    profile: User;

    @Output()
    clickedTag = new EventEmitter<MaxBrainTag>();

    @Output()
    clickedPinModule = new EventEmitter<{ moduleId: string; isPinned: boolean }>();

    @ViewChild('content')
    content: ElementRef;

    @ViewChild('label')
    label: ElementRef;

    @ViewChild('chipList')
    chipList: ElementRef;

    moduleTileIconBottom: number;
    moduleTileIconLeft: number;

    isResized = false;
    /** Bumped after layout/resize so pure `filterTags` remeasures tag row width. */
    tagsLayoutKey = 0;
    ModuleUserProgressStatus = ModuleUserProgressStatus;
    ModuleType = ModuleType;
    ModuleKind = ModuleKind;

    private _resizeSubject = new BehaviorSubject(null);
    resize$ = this._resizeSubject.asObservable().pipe(
        takeUntil(this._unsubscribeAll),
        debounce(() => timer(250))
    );

    unsplashViewsBaseUrl: string;

    moduleStatuses = ModuleStatuses;
    moduleUserCategoryStatus = ModuleUserCategoryStatus;
    moduleCompletitionStatus = ModuleCompletitionStatuses;

    selectedLanguage: ILanguage = null;
    profileLanguage: string;

    @HostListener('window:resize', ['$event'])
    onResize(event): void {
        this._resizeSubject.next(event);
    }

    constructor(
        @Inject(MAXBRAIN_ENVIRONMENT) private _environment: IMaxBrainEnvironment,
        @Inject(MAXBRAIN_LANGUAGE_SERVICE) private _languageService: ILanguageService,
        private _moduleGlobalService: ModuleGlobalService,
        private _translateService: TranslateService,
        private _moduleManagersService: ModuleManagersService,
        private _matDialog: MatDialog,
        private _calculateDuration: CalculateDurationPipe,
        private _changeDetectorRef: ChangeDetectorRef
    ) {
        super();

        this.unsplashViewsBaseUrl = this._environment.unsplashViewsBaseUrl;

        this.selectedLanguage = this._languageService.getLanguageById(this._translateService.currentLang);

        this._translateService.onLangChange.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ lang }) => {
            this.selectedLanguage = this._languageService.getLanguageById(lang);

            this.duration = _calculateDuration.transform(new Date(this.module.startDate?.format()), new Date(this.module.endDate?.format()));
        });

        this.resize$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.duration = _calculateDuration.transform(new Date(this.module.startDate?.format()), new Date(this.module.endDate?.format()));
            this.responsibleManagersString = this._getResponsibleManagersString();
            this._bumpTagsLayout();
        });

        this.selectedLanguage = this._languageService.getLanguageById(this._translateService.currentLang);
        this.profileLanguage = this._translateService.currentLang === 'it-CH' ? 'it-IT' : this._translateService.currentLang;
    }

    ngAfterContentInit(): void {
        setTimeout(() => {
            this._bumpTagsLayout();
            window.dispatchEvent(new Event('resize'));
        }, 0);
    }

    private _bumpTagsLayout(): void {
        this.tagsLayoutKey++;
        this.isResized = true;
        this._changeDetectorRef.markForCheck();
    }

    onPinModule(module: Module, isPinned: boolean): void {
        this.clickedPinModule.emit({ moduleId: module.id, isPinned: isPinned });
    }

    goToModule(module: Module): void {
        // On Team Members page we want to show user state on the module
        if (this.isTeamMembersPage) {
            if (module.kind === ModuleKind.ProgramExecution) {
                this.openTeamProgramMemberDetailsDialog(module);
            }
        } else {
            this._moduleGlobalService.setParentUrl(window.location.pathname);
            this._moduleManagersService.goToModule(this.profile.id, module, 'overview', true);
        }
    }

    openTeamProgramMemberDetailsDialog(currentModule: Module): void {
        const dialog = this._matDialog.open(TeamProgramMemberDetailsDialogComponent, {
            disableClose: false,
            width: '1125px',
            data: {
                moduleId: currentModule.id,
                title: currentModule.name,
                participantId: this.myModuleUser.userId,
            },
        });

        dialog.componentInstance.currentModule = currentModule;
    }

    openTeamCourseMemberDetailsDialog(currentModule: Module): void {
        const dialogRef = this._matDialog.open(TeamCourseMemberDetailsDialogComponent, {
            disableClose: false,
            width: '1125px',
        });

        dialogRef.componentInstance.moduleId = currentModule.id;
        dialogRef.componentInstance.title = currentModule.name;
        dialogRef.componentInstance.participantId = this.myModuleUser.userId;
    }

    private _getResponsibleManagersString(): string {
        let remainingWidth = this.content.nativeElement.offsetWidth - 58 - this.label.nativeElement.offsetWidth;

        const filteredUsers = this.module.responsibleManagers
            .map((user) => user.fullName)
            .filter((fullName, index) => {
                const usersLeft = this.module.responsibleManagers.length - index - 1;
                const nameLength = this.visualLength(usersLeft ? `${fullName} + ${usersLeft}` : fullName);

                if (remainingWidth > nameLength) {
                    remainingWidth -= nameLength;
                    return true;
                }

                return false;
            });

        if (!filteredUsers.length) {
            return this.module.responsibleManagers.length.toString();
        }

        const numberOfHiddenUsers = this.module.responsibleManagers.length - filteredUsers.length;

        return filteredUsers.join(', ') + (numberOfHiddenUsers ? ` + ${numberOfHiddenUsers}` : '');
    }

    visualLength(text: string): number {
        const ruler = document.getElementById('ruler');
        ruler.innerHTML = text;
        const toReturn = ruler.offsetWidth;
        ruler.innerHTML = '';
        return toReturn;
    }

    filterByTag(tag: MaxBrainTag): void {
        this.clickedTag.emit(tag);
    }

    moduleTileIconMouseEntered(event: any): void {
        this.moduleTileIconBottom = event.target.getBoundingClientRect().bottom + 40;
        this.moduleTileIconLeft = event.target.getBoundingClientRect().left + 20;
    }
}
